import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, CalendarToday as CalendarIcon } from '@mui/icons-material';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import api from '../../utils/api';
import ThemeBackground from '../../components/Layout/ThemeBackground';

interface HistoricalSession {
  _id?: string;
  sessionDate: Date | null;
  attended: boolean;
  notes: string;
}

interface EligibilityResponse {
  eligible: boolean;
  reason?: string;
  deadline?: string;
  draftCount?: number;
  clientName?: string;
}

const HistoricalNotes: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [eligible, setEligible] = useState(false);
  const [eligibilityReason, setEligibilityReason] = useState('');
  const [deadline, setDeadline] = useState<Date | null>(null);
  const [clientName, setClientName] = useState('');
  const [sessions, setSessions] = useState<HistoricalSession[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [currentSessionIndex, setCurrentSessionIndex] = useState<number | null>(null);
  const [tempDate, setTempDate] = useState<Date | null>(null);
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');

  // Check eligibility
  useEffect(() => {
    checkEligibility();
  }, [clientId]);

  const checkEligibility = async () => {
    try {
      setLoading(true);
      const response = await api.get<EligibilityResponse>(`/api/historical-notes/clients/${clientId}/eligibility`);
      
      if (response.data.eligible) {
        setEligible(true);
        setClientName(response.data.clientName || '');
        if (response.data.deadline) {
          setDeadline(new Date(response.data.deadline));
        }
        // Load drafts
        await loadDrafts();
      } else {
        setEligible(false);
        setEligibilityReason(response.data.reason || 'Not eligible');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to check eligibility');
      setEligible(false);
    } finally {
      setLoading(false);
    }
  };

  const loadDrafts = async () => {
    try {
      const response = await api.get(`/api/historical-notes/clients/${clientId}/drafts`);
      if (response.data && response.data.length > 0) {
        const loadedSessions = response.data.map((draft: any) => ({
          _id: draft._id,
          sessionDate: new Date(draft.sessionDate),
          attended: draft.attended,
          notes: draft.notes
        }));
        setSessions(loadedSessions);
      } else {
        // Start with one empty session
        setSessions([{ sessionDate: null, attended: true, notes: '' }]);
      }
    } catch (err: any) {
      console.error('Error loading drafts:', err);
      // Start with one empty session
      setSessions([{ sessionDate: null, attended: true, notes: '' }]);
    }
  };

  // Auto-save with debounce
  const autoSave = useCallback(
    async (sessionIndex: number, session: HistoricalSession) => {
      if (!session.sessionDate || !session.notes.trim()) {
        return; // Don't save incomplete sessions
      }

      try {
        setAutoSaveStatus('saving');
        const response = await api.post(`/api/historical-notes/clients/${clientId}/sessions`, {
          _id: session._id,
          sessionDate: session.sessionDate.toISOString(),
          attended: session.attended,
          notes: session.notes
        });

        // Update the session with the returned _id if it's new
        if (!session._id && response.data._id) {
          const updatedSessions = [...sessions];
          updatedSessions[sessionIndex]._id = response.data._id;
          setSessions(updatedSessions);
        }

        setAutoSaveStatus('saved');
        setTimeout(() => setAutoSaveStatus('idle'), 2000);
      } catch (err: any) {
        console.error('Auto-save error:', err);
        setAutoSaveStatus('idle');
      }
    },
    [clientId, sessions]
  );

  // Debounced auto-save
  useEffect(() => {
    const timer = setTimeout(() => {
      sessions.forEach((session, index) => {
        if (session.sessionDate && session.notes.trim()) {
          autoSave(index, session);
        }
      });
    }, 2000);

    return () => clearTimeout(timer);
  }, [sessions, autoSave]);

  const addSession = () => {
    setSessions([...sessions, { sessionDate: null, attended: true, notes: '' }]);
  };

  const removeSession = async (index: number) => {
    const session = sessions[index];
    
    // If it has an _id, delete from backend
    if (session._id) {
      try {
        await api.delete(`/api/historical-notes/clients/${clientId}/sessions/${session._id}`);
      } catch (err: any) {
        setError('Failed to delete session');
        return;
      }
    }

    const newSessions = sessions.filter((_, i) => i !== index);
    setSessions(newSessions.length > 0 ? newSessions : [{ sessionDate: null, attended: true, notes: '' }]);
  };

  const updateSession = (index: number, field: keyof HistoricalSession, value: any) => {
    const newSessions = [...sessions];
    newSessions[index] = { ...newSessions[index], [field]: value };
    setSessions(newSessions);
  };

  const openDatePicker = (index: number) => {
    setCurrentSessionIndex(index);
    setTempDate(sessions[index].sessionDate || new Date());
    setDatePickerOpen(true);
  };

  const handleDateConfirm = () => {
    if (currentSessionIndex !== null && tempDate) {
      updateSession(currentSessionIndex, 'sessionDate', tempDate);
    }
    setDatePickerOpen(false);
    setCurrentSessionIndex(null);
  };

  const validateSessions = (): boolean => {
    setError(null);

    // Check all sessions have dates
    const missingDates = sessions.some(s => !s.sessionDate);
    if (missingDates) {
      setError('All sessions must have a date');
      return false;
    }

    // Check all sessions have notes
    const missingNotes = sessions.some(s => !s.notes.trim() || s.notes === 'Draft in progress...');
    if (missingNotes) {
      setError('All sessions must have notes');
      return false;
    }

    // Check for duplicate dates (compare date only, not time)
    const dates = sessions.map(s => {
      if (!s.sessionDate) return '';
      const d = new Date(s.sessionDate);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    });
    const uniqueDates = new Set(dates);
    if (dates.length !== uniqueDates.size) {
      setError('Cannot have duplicate session dates. Each session must be on a different day.');
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateSessions()) {
      return;
    }

    if (!window.confirm('Are you sure you want to submit these historical notes? This action cannot be undone.')) {
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      // Save all sessions first to ensure they exist in the database
      const savePromises = sessions.map(async (session) => {
        if (session.sessionDate && session.notes.trim()) {
          try {
            const response = await api.post(`/api/historical-notes/clients/${clientId}/sessions`, {
              _id: session._id,
              sessionDate: session.sessionDate.toISOString(),
              attended: session.attended,
              notes: session.notes
            });
            return response.data;
          } catch (err) {
            console.error('Error saving session:', err);
            throw err;
          }
        }
      });

      await Promise.all(savePromises);

      // Now submit
      await api.post(`/api/historical-notes/clients/${clientId}/submit`);

      setSuccess('Historical notes submitted successfully!');
      setTimeout(() => {
        navigate(`/counsellor/clients/${clientId}`);
      }, 2000);

    } catch (err: any) {
      console.error('Submit error:', err);
      setError(err.response?.data?.error || 'Failed to submit historical notes');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <ThemeBackground>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </ThemeBackground>
    );
  }

  if (!eligible) {
    return (
      <ThemeBackground>
        <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4 }}>
          <Alert severity="warning" sx={{ mb: 3 }}>
            {eligibilityReason}
          </Alert>
          <Button variant="outlined" onClick={() => navigate(-1)}>
            Go Back
          </Button>
        </Box>
      </ThemeBackground>
    );
  }

  // Sort sessions by date for display
  const sortedSessions = [...sessions].sort((a, b) => {
    if (!a.sessionDate) return 1;
    if (!b.sessionDate) return -1;
    return a.sessionDate.getTime() - b.sessionDate.getTime();
  });

  return (
    <ThemeBackground>
      <Box sx={{ maxWidth: 1000, mx: 'auto', mt: 4, mb: 8 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" className="theme-text-primary" sx={{ fontWeight: 700, mb: 1 }}>
            Complete Historical Session Notes
          </Typography>
          <Typography variant="body1" className="theme-text-secondary" sx={{ mb: 2 }}>
            Client: <strong>{clientName}</strong>
          </Typography>
          {deadline && (
            <Chip 
              label={`Deadline: ${deadline.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}`}
              color="warning"
              sx={{ mb: 2 }}
            />
          )}
        </Box>

        {/* Warning Banner */}
        <Alert severity="info" sx={{ mb: 4 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            ⚠️ Important Information
          </Typography>
          <Typography variant="body2" component="div">
            • This is a <strong>one-time only</strong> opportunity to add historical session notes<br />
            • <strong>Only include sessions that the client actually attended</strong><br />
            • Add sessions from your previous work with this client (up to yesterday)<br />
            • Your progress is automatically saved as you type<br />
            • Sessions will be numbered chronologically by date once submitted<br />
            • These sessions will count toward your completed sessions but not generate payments
          </Typography>
        </Alert>

        {/* Auto-save status */}
        {autoSaveStatus !== 'idle' && (
          <Box sx={{ mb: 2, textAlign: 'right' }}>
            <Typography variant="caption" className="theme-text-secondary">
              {autoSaveStatus === 'saving' ? '💾 Saving...' : '✓ Saved'}
            </Typography>
          </Box>
        )}

        {/* Error/Success Messages */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        {/* Sessions List */}
        <Box sx={{ mb: 4 }}>
          {sortedSessions.map((session, displayIndex) => {
            const actualIndex = sessions.findIndex(s => s === session);
            return (
              <Box
                key={actualIndex}
                className="glass-card"
                sx={{
                  p: 3,
                  mb: 3,
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '12px'
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" className="theme-text-primary" sx={{ fontWeight: 600 }}>
                    Session {displayIndex + 1}
                  </Typography>
                  <IconButton
                    onClick={() => removeSession(actualIndex)}
                    disabled={sessions.length === 1}
                    sx={{ color: 'rgba(255, 255, 255, 0.6)' }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>

                {/* Date */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" className="theme-text-secondary" sx={{ mb: 1 }}>
                    Session Date *
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<CalendarIcon />}
                    onClick={() => openDatePicker(actualIndex)}
                    sx={{
                      width: '100%',
                      justifyContent: 'flex-start',
                      textTransform: 'none',
                      color: 'rgba(255, 255, 255, 0.9)',
                      borderColor: 'rgba(255, 255, 255, 0.2)',
                      '&:hover': {
                        borderColor: 'rgba(255, 255, 255, 0.3)'
                      }
                    }}
                  >
                    {session.sessionDate
                      ? session.sessionDate.toLocaleDateString('en-GB', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric'
                        })
                      : 'Select date'}
                  </Button>
                </Box>

                {/* Notes */}
                <Box>
                  <Typography variant="body2" className="theme-text-secondary" sx={{ mb: 1 }}>
                    Session Notes *
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={6}
                    value={session.notes}
                    onChange={(e) => updateSession(actualIndex, 'notes', e.target.value.slice(0, 250))}
                    placeholder="Enter session notes..."
                    helperText={`Session notes should be brief which is why they have been restricted to 250 characters. ${session.notes.length}/250`}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        color: 'rgba(255, 255, 255, 0.9)',
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        '& fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.2)'
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.3)'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#667eea'
                        }
                      },
                      '& .MuiFormHelperText-root': {
                        color: 'rgba(255, 255, 255, 0.9)'
                      }
                    }}
                  />
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* Add Session Button */}
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={addSession}
          sx={{
            mb: 4,
            textTransform: 'none',
            color: 'rgba(255, 255, 255, 0.9)',
            borderColor: 'rgba(255, 255, 255, 0.2)',
            '&:hover': {
              borderColor: 'rgba(255, 255, 255, 0.3)',
              backgroundColor: 'rgba(255, 255, 255, 0.05)'
            }
          }}
        >
          Add Another Session
        </Button>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={() => navigate(-1)}
            disabled={submitting}
            sx={{
              textTransform: 'none',
              px: 4,
              borderColor: 'rgba(255, 255, 255, 0.3)',
              color: 'rgba(255, 255, 255, 0.9)',
              '&:hover': {
                borderColor: 'rgba(255, 255, 255, 0.5)'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting || sessions.length === 0}
            sx={{
              textTransform: 'none',
              px: 4,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5568d3 0%, #6a4293 100%)'
              }
            }}
          >
            {submitting ? 'Submitting...' : 'Submit All Sessions'}
          </Button>
        </Box>

        {/* Date Picker Dialog */}
        <Dialog
          open={datePickerOpen}
          onClose={() => setDatePickerOpen(false)}
          maxWidth="sm"
          PaperProps={{
            sx: {
              background: 'rgba(30, 41, 59, 0.95)',
              backdropFilter: 'blur(20px)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '16px'
            }
          }}
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Typography variant="h6" className="theme-text-primary" sx={{ fontWeight: 600 }}>
              Select Session Date
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ display: 'flex', justifyContent: 'center', pb: 2 }}>
            <DatePicker
              selected={tempDate}
              onChange={(date: Date | null) => setTempDate(date)}
              dateFormat="dd/MM/yyyy"
              maxDate={new Date(new Date().setDate(new Date().getDate() - 1))} // Yesterday
              inline
              calendarClassName="historical-notes-datepicker"
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button
              onClick={() => setDatePickerOpen(false)}
              sx={{
                textTransform: 'none',
                color: 'rgba(255, 255, 255, 0.6)',
                '&:hover': {
                  color: 'rgba(255, 255, 255, 0.8)'
                }
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDateConfirm}
              variant="contained"
              sx={{
                textTransform: 'none',
                background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #4338ca 0%, #4f46e5 100%)'
                }
              }}
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </ThemeBackground>
  );
};

export default HistoricalNotes;
